/**
 * Just-intonation targets for the notes of one part, tuned the way a choir
 * tunes: to the chord sounding in all parts at that moment. The chord's
 * root stays where equal temperament puts it (the piano's pitch) and every
 * other tone takes its 5-limit just interval above the root: a major third
 * 13.7 cents low, a minor third 15.6 high, a fifth 2 high, and so on.
 *
 * The harmony is read afresh wherever it changes under a note, so a held
 * note gets a target per chord. A chord is read from the pitch classes
 * sounding (other parts plus the sung note):
 * - shapes cover triads, sevenths, sixths, added ninths, ninths and
 *   suspensions, with the fifth (and in some, the third) allowed to be
 *   missing;
 * - when more than one shape fits (C6 or Am7), the chord is rooted on the
 *   lowest voice that can be its root, then the simpler shape wins;
 * - when nothing fits, one pitch class is set aside as a passing note, the
 *   most briefly held first, whether it is the sung note or another part's.
 * Where no chord can be read (unisons, augmented and diminished-seventh
 * chords, clusters), the target stays equal-tempered.
 */
import { intervalCentsOffset, TUNINGS } from './tuning';

const cents = (ratio, semitones) => 1200 * Math.log2(ratio) - 100 * semitones;
const pitchClass = (midi) => ((Math.round(midi) % 12) + 12) % 12;

// Chord shapes as semitones above the root, simplest first; `optional`
// tones may be missing (the fifth, in practice)
const SHAPES = [
  { tones: [0, 4, 7], optional: [7] }, // major
  { tones: [0, 3, 7], optional: [7] }, // minor
  { tones: [0, 4, 7, 10], optional: [7] }, // dominant 7th
  { tones: [0, 3, 7, 10], optional: [7] }, // minor 7th
  { tones: [0, 4, 7, 11], optional: [7] }, // major 7th
  { tones: [0, 3, 6, 10], optional: [] }, // half-diminished 7th
  { tones: [0, 3, 6], optional: [] }, // diminished
  { tones: [0, 5, 7], optional: [] }, // suspended 4th
  { tones: [0, 5, 7, 10], optional: [7] }, // 7th, suspended 4th
  { tones: [0, 2, 7], optional: [] }, // suspended 2nd
  { tones: [0, 4, 7, 9], optional: [7] }, // major 6th
  { tones: [0, 3, 7, 9], optional: [7] }, // minor 6th
  { tones: [0, 2, 4, 7], optional: [7] }, // added 9th
  { tones: [0, 2, 3, 7], optional: [7] }, // minor, added 9th
  { tones: [0, 2, 4, 7, 10], optional: [7] }, // dominant 9th
  { tones: [0, 2, 4, 7, 11], optional: [7] }, // major 9th
  { tones: [0, 2, 3, 7, 10], optional: [7] }, // minor 9th
  { tones: [0, 2, 4, 7, 9], optional: [7] }, // 6/9
  { tones: [0, 3, 7, 11], optional: [7] }, // minor-major 7th
  { tones: [0, 2, 5, 7, 10], optional: [7] }, // 9th, suspended 4th
  { tones: [0, 7, 10], optional: [7] }, // 7th without a third
  { tones: [0, 7, 11], optional: [] }, // major 7th without a third
  { tones: [0, 2, 7, 10], optional: [7] }, // 9th without a third
  { tones: [0, 7], optional: [] } // open fifth
].map((shape, rank) => ({ ...shape, rank, required: shape.tones.filter(t => !shape.optional.includes(t)) }));

// Cents from equal temperament, by semitones above the root
const SIXTEEN_NINTHS = cents(16 / 9, 10); // minor 7th of a dominant chord, 4 low
const NINE_FIFTHS = cents(9 / 5, 10); // minor 7th of a minor chord, 18 high
const CHORD_TONE_CENTS = {
  0: 0,
  2: cents(9 / 8, 2),
  3: cents(6 / 5, 3),
  4: cents(5 / 4, 4),
  5: cents(4 / 3, 5),
  7: cents(3 / 2, 7),
  9: cents(5 / 3, 9),
  11: cents(15 / 8, 11)
};

/**
 * Readings of the pitch classes in `voicing` (ordered from the lowest
 * sounding upwards) that fit a chord shape, best first: rooted on the
 * lowest voice that can be the root, then the simplest shape. Empty when
 * nothing fits.
 */
export function readChord(voicing) {
  const present = [...new Set(voicing)];
  if (present.length < 2) return [];
  const readings = [];
  for (const shape of SHAPES) {
    for (const root of present) {
      const intervals = present.map(pc => (pc - root + 12) % 12);
      if (intervals.every(i => shape.tones.includes(i)) && shape.required.every(i => intervals.includes(i))) {
        readings.push({ root, shape, intervals });
      }
    }
  }
  return readings.sort((a, b) => present.indexOf(a.root) - present.indexOf(b.root) || a.shape.rank - b.shape.rank);
}

/** Cents from equal temperament for a pitch class in a chord reading. */
export function centsInChord({ root, intervals }, pc) {
  const interval = (pc - root + 12) % 12;
  // The seventh sits a minor third above the fifth (9/5) in minor and
  // half-diminished chords; in dominant chords, suspended or not, and
  // where there is no third, it is the lower 16/9
  if (interval === 10) return intervals.includes(3) ? NINE_FIFTHS : SIXTEEN_NINTHS;
  // A diminished fifth over a minor third (64/45); otherwise the tritone
  // is an augmented fourth (45/32)
  if (interval === 6) return intervals.includes(3) ? cents(64 / 45, 6) : intervalCentsOffset(6, TUNINGS.just);
  if (interval in CHORD_TONE_CENTS) return CHORD_TONE_CENTS[interval];
  return intervalCentsOffset(interval, TUNINGS.just);
}

/**
 * The just target, in cents from equal temperament, for the sung note
 * (`note`: { midi, start, end }) while `sounding` (other parts' notes, same
 * shape) are heard. Null when no chord can be read.
 */
export function chordCents(note, sounding) {
  const own = pitchClass(note.midi);
  const all = [...sounding, note];
  const read = (notes) => {
    const voicing = [...notes].sort((a, b) => a.midi - b.midi).map(n => pitchClass(n.midi));
    const [best] = readChord(voicing);
    return best ? centsInChord(best, own) : null;
  };

  // The chord with the sung note in it
  const result = read(all);
  if (result !== null) return result;

  // Something sounding is a passing note: set aside one pitch class, the
  // most briefly held first (the sung note included), and read again
  const held = new Map();
  all.forEach((n) => {
    const pc = pitchClass(n.midi);
    held.set(pc, (held.get(pc) || 0) + (n.end - n.start));
  });
  for (const [pc] of [...held.entries()].sort((a, b) => a[1] - b[1])) {
    const reading = read(all.filter(n => pitchClass(n.midi) !== pc));
    if (reading !== null) return reading;
  }
  return null;
}

const round1 = (value) => Math.round(value * 10) / 10;

/**
 * notes: the part's notes { start, end, midi, ... }; others: every other
 * part's notes { start, end, midi }. Returns the notes with `tuning`, the
 * just target per stretch of unchanging harmony ([{ from, to, cents }],
 * cents from equal temperament), and `cents`, the target at the note's
 * middle.
 */
export function justTargets(notes, others) {
  const sorted = [...others].sort((a, b) => a.start - b.start);
  const longest = sorted.reduce((max, n) => Math.max(max, n.end - n.start), 0);
  return notes.map((note) => {
    // Other parts' notes overlapping this one
    const overlapping = [];
    for (const other of sorted) {
      if (other.start >= note.end) break;
      if (other.start < note.start - longest) continue;
      if (other.end > note.start) overlapping.push(other);
    }
    // Stretches between the moments the harmony changes
    const cuts = new Set([note.start, note.end]);
    overlapping.forEach((other) => {
      if (other.start > note.start && other.start < note.end) cuts.add(other.start);
      if (other.end > note.start && other.end < note.end) cuts.add(other.end);
    });
    const times = [...cuts].sort((a, b) => a - b);
    const tuning = [];
    for (let i = 0; i < times.length - 1; i++) {
      const middle = (times[i] + times[i + 1]) / 2;
      const sounding = overlapping.filter(o => o.start <= middle && middle < o.end);
      const value = round1(chordCents(note, sounding) ?? 0);
      const last = tuning[tuning.length - 1];
      if (last && last.cents === value) last.to = times[i + 1];
      else tuning.push({ from: times[i], to: times[i + 1], cents: value });
    }
    const middle = (note.start + note.end) / 2;
    const atMiddle = tuning.find(t => t.from <= middle && middle < t.to) || tuning[tuning.length - 1];
    return { ...note, tuning, cents: atMiddle ? atMiddle.cents : 0 };
  });
}
